import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import DateInput from "@/components/DateInput";
import { formatRut, isValidRut, cleanRut } from "@/lib/rut";
import { normalizeClPhone, isValidClPhone, CL_PREFIX, normalizeClWhatsapp, isValidClWhatsapp, WA_PREFIX } from "@/lib/phone";

const EMPTY = { rut: "", first_name: "", last_name: "", email: "", phone: "+56", whatsapp: "+569", city: "", position: "", company: "", mandante: "", contrato: "", project: "", assigned_user: "", expected_hire_date: "", whatsapp_opt_in: true, visa_temporaria: false };

function Field({ name, label, type = "text", required, value, onChange, error, placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        data-testid={`worker-field-${name}`}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none text-sm ${error ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-blue-400"}`}
      />
      {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

export default function WorkerFormDialog({ open, onClose, onDone, projects }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [mandantes, setMandantes] = useState([]);
  const [cargos, setCargos] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/mandantes").then((r) => setMandantes(r.data)).catch(() => {});
  }, [open]);

  const mandanteObj = mandantes.find((m) => m.name === form.mandante);
  const contratos = mandanteObj?.contratos || [];
  const contratoObj = contratos.find((c) => c.name === form.contrato);

  useEffect(() => {
    setCargos([]);
    setForm((f) => ({ ...f, position: "" }));
    if (!mandanteObj || !contratoObj) return;
    api.get(`/mandantes/${mandanteObj.id}/contratos/${contratoObj.id}/cargos`)
      .then((r) => setCargos(r.data || []))
      .catch(() => setCargos([]));
  }, [form.mandante, form.contrato]);

  const rutClean = cleanRut(form.rut);
  const rutError = form.rut && rutClean.length >= 2 && !isValidRut(form.rut) ? "RUT inválido" : "";
  const phoneError = form.phone && form.phone !== CL_PREFIX && !isValidClPhone(form.phone) ? "Teléfono inválido (+56 + 9 dígitos)" : "";

  const handleRutChange = (e) => set("rut", formatRut(cleanRut(e.target.value).slice(0, 9)));
  const handlePhoneChange = (e) => set("phone", normalizeClPhone(e.target.value));
  const handleWhatsappChange = (e) => {
    let v = e.target.value || "";
    if (!v.startsWith(WA_PREFIX)) {
      const digits = v.replace(/\D/g, "");
      const rest = digits.startsWith("569") ? digits.slice(3) : digits;
      v = WA_PREFIX + rest;
    } else {
      const rest = v.slice(WA_PREFIX.length).replace(/\D/g, "");
      v = WA_PREFIX + rest.slice(0, 8);
    }
    set("whatsapp", v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.rut && !isValidRut(form.rut)) { toast.error("RUT inválido"); return; }
    if (form.phone && form.phone !== CL_PREFIX && !isValidClPhone(form.phone)) { toast.error("Teléfono inválido"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        company: form.mandante || form.company,
        rut: form.rut ? formatRut(form.rut) : "",
        phone: form.phone === CL_PREFIX ? "" : form.phone,
        whatsapp: form.whatsapp === WA_PREFIX ? "" : form.whatsapp,
        expected_hire_date: form.expected_hire_date ? new Date(form.expected_hire_date).toISOString() : null,
      };
      await api.post("/workers", payload);
      toast.success("Trabajador creado");
      setForm(EMPTY); onDone && onDone(); onClose();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "No se pudo crear el trabajador");
    }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserPlus size={18} className="text-blue-600" /> Nuevo trabajador</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field name="first_name" label="Nombre" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
          <Field name="last_name" label="Apellido" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
          <Field name="rut" label="RUT" value={form.rut} onChange={handleRutChange} placeholder="12.345.678-9" error={rutError} />
          <Field name="email" label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Field name="phone" label="Teléfono" value={form.phone} onChange={handlePhoneChange} placeholder="+56XXXXXXXXX" error={phoneError} />
          <Field name="whatsapp" label="WhatsApp" value={form.whatsapp} onChange={handleWhatsappChange} placeholder="+569XXXXXXXX" />
          <div>
            <label className="text-xs font-medium text-slate-600">Mandante</label>
            <select data-testid="worker-field-mandante" value={form.mandante}
              onChange={(e) => { set("mandante", e.target.value); set("contrato", ""); }}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm bg-white">
              <option value="">—</option>
              {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Contrato</label>
            <select data-testid="worker-field-contrato" value={form.contrato}
              onChange={(e) => set("contrato", e.target.value)} disabled={!form.mandante}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm bg-white disabled:bg-slate-50">
              <option value="">—</option>
              {contratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-span-1">
            <label className="text-xs font-medium text-slate-600">Cargo</label>
            {cargos.length > 0 ? (
              <select data-testid="worker-field-position" value={form.position}
                onChange={(e) => set("position", e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm bg-white">
                <option value="">—</option>
                {cargos.map((cg) => <option key={cg.id} value={cg.name}>{cg.name}</option>)}
              </select>
            ) : (
              <input data-testid="worker-field-position" value={form.position}
                onChange={(e) => set("position", e.target.value)}
                placeholder={form.contrato ? "Texto libre (contrato sin cargos)" : "Elige contrato primero"}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm" />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Proyecto</label>
            <select data-testid="worker-field-project" value={form.project} onChange={(e) => set("project", e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm bg-white">
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <Field name="city" label="Ciudad" value={form.city} onChange={(e) => set("city", e.target.value)} />
          <div>
            <label className="text-xs font-medium text-slate-600">Fecha de ingreso</label>
            <div className="mt-1">
              <DateInput testid="worker-field-expected_hire_date"
                value={form.expected_hire_date}
                onChange={(iso) => set("expected_hire_date", iso)} />
            </div>
          </div>
          <Field name="assigned_user" label="Responsable" value={form.assigned_user} onChange={(e) => set("assigned_user", e.target.value)} />
          <div className="col-span-2 flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <div><p className="text-sm font-medium text-slate-700">Visa temporaria</p><p className="text-xs text-slate-400">Activa requiere Pasaporte en documentos.</p></div>
            <Switch data-testid="worker-field-visa" checked={form.visa_temporaria} onCheckedChange={(v) => set("visa_temporaria", v)} />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-slate-500 px-4 py-2">Cancelar</button>
            <button data-testid="worker-form-submit" disabled={loading || !!rutError || !!phoneError} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
              {loading && <Loader2 size={15} className="animate-spin" />} Crear
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
